/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@emotion/react"
import { Typography, Box, RadioGroup, FormControlLabel, Radio, Checkbox, Tooltip, InputAdornment, TextField } from "@mui/material"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MyInput } from "../../../components/input/MyInput"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    text-align: center;
    max-width: 700px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 100%;
`)

const RadioGroupStyle = withMyTheme((theme) => css`
    width: 100%;
    gap: 1rem;
`)

const RadioLabelStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 1rem;
    border: 2px solid ${theme.palette.divider};
    border-radius: 12px;
    transition: all 0.2s;
    
    &:hover {
        border-color: ${theme.palette.primary.main};
        background-color: ${theme.palette.action.hover};
    }
    
    &.selected {
        border-color: ${theme.palette.primary.main}; 
    }
    
    .MuiRadio-root {
        color: ${theme.palette.primary.main};
    }
`)

const StyledRadioStyle = withMyTheme((theme) => css`
    &.MuiRadio-root {
        color: ${theme.palette.primary.main};
    }
`)

const LabelTextStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
`)

const InfoIconStyle = withMyTheme((theme) => css`
    color: ${theme.palette.text.secondary};
    font-size: 1.2rem;
    cursor: help;
`)

const TooltipContentStyle = withMyTheme((theme) => css`
    white-space: pre-line;
    font-size: 0.875rem;
    line-height: 1.5;
`)

const ConditionalFieldStyle = withMyTheme((theme) => css`
    width: 100%;
    margin-top: 1rem;
`)

const SuffixStyle = withMyTheme((theme) => css`
    color: ${theme.palette.text.primary};
    font-weight: 500;
    white-space: nowrap;
    padding-right: 14px;
`)

const SubdomainTextFieldStyle = withMyTheme((theme) => css`
    width: calc(100% - 2rem);
    margin: 0 1rem;
    
    & .MuiInputLabel-root {
        font-family: ${theme.typography.body1.fontFamily};
        color: ${theme.palette.text.primary};
        
        &.Mui-focused {
            color: ${theme.palette.primary.main};
        }
    }
    
    & .MuiOutlinedInput-root {
        border-radius: 12px;
        background-color: white;
        
        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
        }
        
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
            border-width: 2px;
        }
    }
    
    .MuiInputLabel-root {
        background: white;
        padding: 0 6px;
    }
`)

const CheckboxContainerStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    margin-top: 1rem;
    
    .MuiFormControlLabel-root {
        margin: 0;
    }
`)

const CheckboxLabelStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`)

interface DomainSelectionStepProps {
    onStart?: () => void
}

export const DomainSelectionStep = ({}: DomainSelectionStepProps) => {
    const { t } = useTranslation()
    const { formData, updateDomain } = useQuestionnaireForm()

    const handleDomainTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'subdomain' | 'custom'
        updateDomain({ type: value })
    }

    const handleSubdomainNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateDomain({ subdomainName: event.target.value })
    }

    const handleCustomDomainChange = (value: string) => {
        updateDomain({ customDomain: value })
    }

    const handleLetUsBuyDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateDomain({ letUsBuyDomain: event.target.checked })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.domain.title')}
            </Typography>
            
            <Box css={FormContainerStyle}>
                <RadioGroup
                    value={formData.domain.type}
                    onChange={handleDomainTypeChange}
                    css={RadioGroupStyle}
                >
                    {/* Subdomain Option */}
                    <Box>
                        <FormControlLabel
                            value="subdomain"
                            control={<Radio css={StyledRadioStyle} />}
                            label={
                                <Box css={LabelTextStyle}>
                                    <span>{t('questionnaire.domain.subdomainOption')}</span>
                                    <Tooltip 
                                        title={<span css={TooltipContentStyle}>{t('questionnaire.domain.subdomainInfo')}</span>} 
                                        arrow
                                        placement="right"
                                    >
                                        <InfoOutlinedIcon css={InfoIconStyle} />
                                    </Tooltip>
                                </Box>
                            }
                            css={RadioLabelStyle}
                            className={formData.domain.type === 'subdomain' ? 'selected' : ''}
                        />
                        {formData.domain.type === 'subdomain' && (
                            <Box css={ConditionalFieldStyle}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t('questionnaire.domain.subdomainLabel')}
                                    placeholder={t('questionnaire.domain.subdomainPlaceholder')}
                                    value={formData.domain.subdomainName}
                                    onChange={handleSubdomainNameChange}
                                    css={SubdomainTextFieldStyle}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography css={SuffixStyle}>
                                                    .weddie.site
                                                </Typography>
                                            </InputAdornment>
                                        )
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Custom Domain Option */}
                    <Box>
                        <FormControlLabel
                            value="custom"
                            control={<Radio css={StyledRadioStyle} />}
                            label={
                                <Box css={LabelTextStyle}>
                                    <span>{t('questionnaire.domain.customDomainOption')}</span>
                                    <Tooltip 
                                        title={<span css={TooltipContentStyle}>{t('questionnaire.domain.customDomainInfo')}</span>} 
                                        arrow
                                        placement="right"
                                    >
                                        <InfoOutlinedIcon css={InfoIconStyle} />
                                    </Tooltip>
                                </Box>
                            }
                            css={RadioLabelStyle}
                            className={formData.domain.type === 'custom' ? 'selected' : ''}
                        />
                        {formData.domain.type === 'custom' && (
                            <Box css={ConditionalFieldStyle}>
                                <MyInput
                                    label={t('questionnaire.domain.customDomainLabel')}
                                    placeholder={t('questionnaire.domain.customDomainPlaceholder')}
                                    value={formData.domain.customDomain}
                                    onChange={handleCustomDomainChange}
                                    additionalCss={(theme: any) => css`
                                        width: calc(100% - 2rem);
                                        margin: 0 1rem;
                                    `}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                                <Box css={CheckboxContainerStyle}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.domain.letUsBuyDomain}
                                                onChange={handleLetUsBuyDomainChange}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box css={CheckboxLabelStyle}>
                                                <span>{t('questionnaire.domain.letUsBuyDomain')}</span>
                                                <Tooltip 
                                                    title={<span css={TooltipContentStyle}>{t('questionnaire.domain.letUsBuyDomainInfo')}</span>}
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlinedIcon css={InfoIconStyle} />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </RadioGroup>
            </Box>
        </div>
    )
}
